import axios from 'axios';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { useNotificationStore } from '../../../stores/notificationStore';
import { useUserStore } from '../../../stores/userStore';

const PersonalInfo = ({ user }) => {
	const { updateUser } = useUserStore();
	const { showSuccess, showError } = useNotificationStore();

	const [isSaving, setIsSaving] = useState(false);
	const [nameFirst, setNameFirst] = useState(user.name_first);
	const [nameLast, setNameLast] = useState(user.name_last);
	const [address, setAddress] = useState(user.address || '');
	const [city, setCity] = useState(user.city || '');
	const [state, setState] = useState(user.state || '');
	const [zip, setZip] = useState(user.zip || '');
	const [birthDate, setBirthDate] = useState(user.birth_dttm.split('T')[0]);
	const [email, setEmail] = useState(user.email || '');
	const [phoneMobile, setPhoneMobile] = useState(user.phone_mobile || '');
	const [maritalStatus, setMaritalStatus] = useState(user.marital_status || '');
	const [disabilityStatus, setDisibilityStatus] = useState(
		user.disability_status || '',
	);
	const [veteranStatus, setVeteranStatus] = useState(user.veteran_status || '');
	const [ethnicity, setEthnicity] = useState(user.ethnicity || '');
	const [race, setRace] = useState(user.race || '');
	const [genderIdent, setGenderIdent] = useState(user.gender || '');
	const [last4SSN, setLast4SSN] = useState(user.last_4_ssn || '');

	const [errorDetails, setErrorDetails] = useState(null);

	const handleUpdateProfile = async (event) => {
		event.preventDefault();
		setErrorDetails(null);
		setIsSaving(true);
		// TODO validation
		try {
			const { data } = await axios.put(`/clients/${user.id}`, {
				name_first: nameFirst,
				name_last: nameLast,
				address,
				city,
				state,
				zip,
				email,
				phone_mobile: phoneMobile,
				birth_dttm: birthDate,
				marital_status: maritalStatus,
				disability_status: disabilityStatus,
				veteran_status: veteranStatus,
				ethnicity,
				race,
				gender: genderIdent,
				last_4_ssn: last4SSN,
			});
			updateUser(data);
			showSuccess('Profile updated');
			setIsSaving(false);
			// clear fields
		} catch (err) {
			if (err?.response?.data?.error) {
				const { details } = err.response.data.error;
				setErrorDetails(details);
			} else {
				showError('Password not changed');
			}
			setIsSaving(false);
		}
	};

	return (
		<div className="grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-3">
			<div className="px-4 sm:px-0">
				<h2>Profile</h2>
				<p className="mt-1 text-sm leading-6 text-gray-600">
					Manage your profile and personal information. By adding your mobile
					phone or email address you will have been access and support while
					using Tandem.
				</p>
			</div>
			<form className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-2">
				<div className="card-body">
					<div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
						<div className="form-control sm:col-span-3">
							<label htmlFor="name-first" className="label label-text">
								First Name
							</label>
							<input
								id="name-first"
								name="name_first"
								type="text"
								autoComplete="given-name"
								required
								className="input input-sm input-bordered"
								value={nameFirst}
								onChange={({ target }) => setNameFirst(target.value)}
							/>
						</div>
						<div className="form-control sm:col-span-3">
							<label htmlFor="name-last" className="label label-text">
								Last Name
							</label>
							<input
								id="name-last"
								name="name_last"
								type="text"
								autoComplete="family-name"
								required
								className="input input-sm input-bordered"
								value={nameLast}
								onChange={({ target }) => setNameLast(target.value)}
							/>
						</div>
						<div className="form-control sm:col-span-4">
							<label htmlFor="address" className="label label-text">
								Address
							</label>
							<input
								id="address"
								name="address"
								type="text"
								autoComplete="street-address"
								required
								className="input input-sm input-bordered"
								value={address}
								onChange={({ target }) => setAddress(target.value)}
							/>
						</div>
						<div className="form-control sm:col-span-3">
							<label htmlFor="city" className="label label-text">
								City
							</label>
							<input
								id="city"
								name="city"
								type="text"
								autoComplete="address-level2"
								required
								className="input input-sm input-bordered"
								value={city}
								onChange={({ target }) => setCity(target.value)}
							/>
						</div>
						<div className="form-control sm:col-span-2">
							<label htmlFor="state" className="label label-text">
								State
							</label>
							<input
								id="state"
								name="state"
								type="text"
								autoComplete="address-level1"
								required
								className="input input-sm input-bordered"
								value={state}
								onChange={({ target }) => setState(target.value)}
							/>
						</div>
						<div className="form-control sm:col-span-1">
							<label htmlFor="zip" className="label label-text">
								Zip
							</label>
							<input
								id="zip"
								name="zip"
								type="text"
								autoComplete="postal-code"
								required
								className="input input-sm input-bordered"
								value={zip}
								onChange={({ target }) => setZip(target.value)}
							/>
						</div>
						<div className="form-control sm:col-span-3">
							<label htmlFor="dob" className="label label-text">
								Date of Birth
							</label>
							<input
								id="dob"
								name="birth_dttm"
								type="date"
								autoComplete="bday"
								required
								className="input input-sm input-bordered"
								value={birthDate}
								onChange={({ target }) => setBirthDate(target.value)}
							/>
						</div>
						<div className="form-control sm:col-span-3">
							<label htmlFor="gender" className="label label-text">
								Gender Identification
							</label>
							<select
								id="gender"
								className="select select-bordered select-sm"
								value={genderIdent}
								onChange={({ target }) => setGenderIdent(target.value)}
							>
								<option value=""></option>
								<option value="woman">Woman</option>
								<option value="man">Man</option>
								<option value="trans_woman">Transgender Woman</option>
								<option value="trans_man">Transgender Man</option>
								<option value="non_binary">Non-binary</option>
								<option value="agender">
									Agender/I don&apos;t identify with a gender
								</option>
								<option value="other">Gender not listed</option>
								<option value="decline_to_state">Prefer not to state</option>
							</select>
						</div>
						<label className="form-control sm:col-span-3" htmlFor="email">
							<div className="label label-text">
								<span className="label-text">Email Address</span>
							</div>
							<input
								id="email"
								name="email"
								type="email"
								autoComplete="email"
								className={cn('input', 'input-sm', 'input-bordered', {
									'input-error': errorDetails?.messages?.email,
								})}
								value={email}
								onChange={({ target }) => setEmail(target.value)}
							/>
							{errorDetails?.messages?.email && (
								<div className="label label-text">
									<span className="label-text-alt text-error">
										{errorDetails.messages.email}
									</span>
								</div>
							)}
						</label>
						<div className="form-control sm:col-span-3">
							<label htmlFor="phone-mobile" className="label label-text">
								Mobile Phone
							</label>
							<input
								id="phone-mobile"
								name="phone_mobile"
								type="text"
								autoComplete="tel"
								className="input input-sm input-bordered"
								value={phoneMobile}
								onChange={({ target }) => setPhoneMobile(target.value)}
							/>
						</div>
						<div className="form-control sm:col-span-3">
							<label htmlFor="marital-status" className="label label-text">
								Marital Status
							</label>
							<select
								id="marital-status"
								className="select select-bordered select-sm"
								value={maritalStatus}
								onChange={({ target }) => setMaritalStatus(target.value)}
							>
								<option value=""></option>
								<option value="single">Single</option>
								<option value="married">Married</option>
								<option value="divorced">Divorced</option>
								<option value="widowed">Widowed</option>
							</select>
						</div>
						<div className="form-control sm:col-span-3">
							<label htmlFor="disability-status" className="label label-text">
								Disability
							</label>
							<select
								id="disability-status"
								className="select select-bordered select-sm"
								value={disabilityStatus}
								onChange={({ target }) => setDisibilityStatus(target.value)}
							>
								<option value=""></option>
								<option value="no">No</option>
								<option value="yes">Yes</option>
								<option value="decline">Decline to answer</option>
							</select>
						</div>
						<div className="form-control sm:col-span-3">
							<label htmlFor="veteran-status" className="label label-text">
								Veteran
							</label>
							<select
								id="veteran-status"
								className="select select-bordered select-sm"
								value={veteranStatus}
								onChange={({ target }) => setVeteranStatus(target.value)}
							>
								<option value=""></option>
								<option value="no">No</option>
								<option value="yes">Yes</option>
								<option value="decline">Decline to answer</option>
							</select>
						</div>
						<div className="form-control sm:col-span-3">
							<label htmlFor="ethnicity" className="label label-text">
								Ethnicity
							</label>
							<select
								id="ethnicity"
								className="select select-bordered select-sm"
								value={ethnicity}
								onChange={({ target }) => setEthnicity(target.value)}
							>
								<option value=""></option>
								<option value="yes">Hispanic or Latino</option>
								<option value="no">Not Hispanic or Latino</option>
								<option value="decline">Decline to answer</option>
							</select>
						</div>
						<div className="form-control sm:col-span-3">
							<label htmlFor="race" className="label label-text">
								Race
							</label>
							<select
								id="race"
								className="select select-bordered select-sm"
								value={race}
								onChange={({ target }) => setRace(target.value)}
							>
								<option value=""></option>
								<option value="amer_indian">
									American Indian or Alaska Native
								</option>
								<option value="asian">Asian</option>
								<option value="black">Black or African American</option>
								<option value="native_hawaiian_islander">
									Native Hawaiian or Other Pacific Islander
								</option>
								<option value="white">White</option>
								<option value="other">Other race</option>
								<option value="decline">Decline to answer</option>
							</select>
						</div>
						<div className="form-control sm:col-span-2">
							<label htmlFor="zip" className="label label-text">
								4 Digit Verification Number
							</label>
							<input
								id="last4SSN"
								name="last4SSN"
								type="text"
								autoComplete="none"
								className="input input-sm input-bordered"
								value={last4SSN}
								maxLength={4}
								onChange={({ target }) => setLast4SSN(target.value)}
							/>
						</div>
					</div>
				</div>
				<div className="justify-end px-8 py-4 border-t card-actions border-gray-900/10">
					<button
						className="text-white btn btn-primary btn-sm"
						name="form"
						value="personalInfo"
						disabled={isSaving}
						onClick={handleUpdateProfile}
					>
						Save
					</button>
				</div>
			</form>
		</div>
	);
};

PersonalInfo.propTypes = { user: PropTypes.object.isRequired };

export default PersonalInfo;
